import { useState, useCallback, useEffect } from "react";
import { useSnackbar } from "notistack";

export interface Announcement {
  id: string;
  title: string;
  content: string;
  type: "INFO" | "WARNING" | "SUCCESS" | "ERROR" | "SYSTEM";
  category: "MAINTENANCE" | "FEATURE" | "UPDATE" | "OUTAGE" | "PROMOTION" | "GENERAL";
  target: string;
  audience: string;
  deviceType: string;
  status: "SCHEDULED" | "ACTIVE" | "INACTIVE" | "CANCELLED";
  publishedAt: string;
  unpublishedAt?: string;
  createdAt: string;
  updatedAt: string;
}

export interface AnnouncementComment {
  id: string;
  content: string;
  parentId: string | null;
  user: {
    id: string;
    firstName?: string;
    lastName?: string;
    avatar?: string;
  };
  createdAt: string;
  replies?: AnnouncementComment[];
}

export interface AnnouncementReactions {
  [key: string]: number;
}

// In a real application, you'd replace this with your actual API fetcher.
// Assuming a generic API fetcher or fetch API is available, we will mock for now or use the standard fetch.
// Actually, looking at other hooks, we can just use `fetch` with token.

const API_URL = process.env.REACT_APP_API_URL || "/api";

const getAuthToken = () => {
  return localStorage.getItem("token") || "";
};

const fetchWithAuth = async (url: string, options: RequestInit = {}) => {
  const token = getAuthToken();
  const headers = {
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...options.headers,
  };
  const response = await fetch(url, { ...options, headers });
  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.message || "An error occurred");
  }
  return data;
};

export const useAnnouncements = () => {
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  const fetchAnnouncements = useCallback(async (params: { page?: number; limit?: number; status?: string; type?: string; category?: string } = {}) => {
    setLoading(true);
    try {
      const query = new URLSearchParams(params as any).toString();
      const res = await fetchWithAuth(`${API_URL}/announcements?${query}`);
      if (res.success) {
        setAnnouncements(res.data);
        setTotal(res.total);
      }
    } catch (error: any) {
      enqueueSnackbar(error.message || "Failed to fetch announcements", { variant: "error" });
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar]);

  const createAnnouncement = async (data: Partial<Announcement>): Promise<boolean> => {
    try {
      const res = await fetchWithAuth(`${API_URL}/announcements`, {
        method: "POST",
        body: JSON.stringify(data),
      });
      if (res.success) {
        enqueueSnackbar("Announcement created successfully", { variant: "success" });
        fetchAnnouncements();
        return true;
      }
      return false;
    } catch (error: any) {
      enqueueSnackbar(error.message || "Failed to create announcement", { variant: "error" });
      return false;
    }
  };

  const updateAnnouncement = async (id: string, data: Partial<Announcement>): Promise<boolean> => {
    try {
      const res = await fetchWithAuth(`${API_URL}/announcements/${id}`, {
        method: "PUT",
        body: JSON.stringify(data),
      });
      if (res.success) {
        enqueueSnackbar("Announcement updated successfully", { variant: "success" });
        fetchAnnouncements();
        return true;
      }
      return false;
    } catch (error: any) {
      enqueueSnackbar(error.message || "Failed to update announcement", { variant: "error" });
      return false;
    }
  };

  const deleteAnnouncement = async (id: string): Promise<boolean> => {
    try {
      const res = await fetchWithAuth(`${API_URL}/announcements/${id}`, {
        method: "DELETE",
      });
      if (res.success) {
        enqueueSnackbar("Announcement deleted successfully", { variant: "success" });
        fetchAnnouncements();
        return true;
      }
      return false;
    } catch (error: any) {
      enqueueSnackbar(error.message || "Failed to delete announcement", { variant: "error" });
      return false;
    }
  };

  const getComments = async (announcementId: string): Promise<AnnouncementComment[]> => {
    try {
      const res = await fetchWithAuth(`${API_URL}/announcements/${announcementId}/comments`);
      if (res.success) return res.data;
    } catch (error: any) {
      console.error(error);
    }
    return [];
  };

  const addComment = async (announcementId: string, content: string, parentId?: string | null) => {
    try {
      const res = await fetchWithAuth(`${API_URL}/announcements/${announcementId}/comments`, {
        method: "POST",
        body: JSON.stringify({ content, parentId }),
      });
      return res.success;
    } catch (error: any) {
      enqueueSnackbar(error.message || "Failed to add comment", { variant: "error" });
      return false;
    }
  };

  const deleteComment = async (commentId: string) => {
    try {
      const res = await fetchWithAuth(`${API_URL}/announcements/comments/${commentId}`, {
        method: "DELETE",
      });
      if (res.success) {
        enqueueSnackbar("Comment deleted", { variant: "success" });
        return true;
      }
    } catch (error: any) {
      enqueueSnackbar(error.message || "Failed to delete comment", { variant: "error" });
      return false;
    }
  };

  const getReactions = async (announcementId: string): Promise<AnnouncementReactions> => {
    try {
      const res = await fetchWithAuth(`${API_URL}/announcements/${announcementId}/reactions`);
      if (res.success) return res.data;
    } catch (error: any) {
      console.error(error);
    }
    return {};
  };

  const toggleReaction = async (announcementId: string, reactionType: string) => {
    try {
      const res = await fetchWithAuth(`${API_URL}/announcements/${announcementId}/reactions`, {
        method: "POST",
        body: JSON.stringify({ reactionType }),
      });
      return res.success;
    } catch (error: any) {
      console.error(error);
      return false;
    }
  };

  return {
    announcements,
    total,
    loading,
    fetchAnnouncements,
    createAnnouncement,
    updateAnnouncement,
    deleteAnnouncement,
    getComments,
    addComment,
    deleteComment,
    getReactions,
    toggleReaction,
  };
};
